import * as Sentry from "@sentry/browser";

// The DSN is deliberately committed rather than injected as a build variable. A
// Sentry DSN is not a credential — it is designed to ship inside the client bundle,
// and this project's is already public: Sentry serves it unauthenticated inside the
// loader script at js.sentry-cdn.com. Committing it keeps `yarn build` working with
// no configuration, which matters because CI runs a bare `yarn build` with no
// secrets, and it keeps build-affecting config in the repo rather than only in the
// Amplify console.
const DSN = "https://83cb759b517aebd73c5db7e251104452@o3809.ingest.us.sentry.io/4511810018738177";

// __AG_ENVIRONMENT__ is replaced at build time by esbuild — see `js:env` in package.json,
// which crosses Amplify's ENV with AWS_BRANCH so a PR preview is distinguishable from a
// laptop, the one distinction ENV cannot draw on its own (#123). This replaced a
// client-side hostname match (#113): the same values, resolved where the build already
// knows the answer, which also means a connected `develop` reports as staging rather than
// falling through to production.
//
// The typeof guard keeps a hand-rolled esbuild invocation without the define from throwing
// at load. Both js:build and js:watch set it, and esbuild substitutes inside the typeof
// too, so the guard costs nothing whenever the define is present.
const environment = typeof __AG_ENVIRONMENT__ === "undefined" ? "development" : __AG_ENVIRONMENT__;

// Errors only — no tracing. Sentry's default `init` includes browser tracing, but on
// a static site with no XHR the only things it buys are Web Vitals and a pageload
// span, which Amplify, CrUX and GA already report. It cost 19KB gzipped of a 47KB
// regression, so it is stripped rather than sampled down: `browserTracingIntegration`
// is omitted here and `__SENTRY_TRACING__=false` removes the machinery behind it at
// build time. Errors are the signal this site actually lacked.
Sentry.init({
    dsn: DSN,
    environment,

    // `release` is absent here on purpose. The SDK picks it up itself from the
    // `__SENTRY_RELEASE__` magic string, which `js:build` replaces with Amplify's
    // `AWS_COMMIT_ID` (see @sentry/browser's applyDefaultOptions). Setting it here too
    // would just duplicate that with a second mechanism that could drift.
    //
    // It matters because browserSessionIntegration, a v10 default, posts a session
    // envelope on every pageload for release health — data that has nothing to attribute
    // itself to unless a release is set. The define falls back to "development" so a bare
    // `yarn build` with no environment still works, which is the condition CI builds under.

    // Never transmit from a locally-built bundle. Tagging alone is not enough — the
    // SDK would still spend quota and bury real errors under our own reloads, since a
    // v10 default (`browserSessionIntegration`) posts a session envelope on every
    // pageload regardless of whether anything went wrong. A build without AWS_BRANCH
    // always bakes "development" whatever ENV says (lib/environment.js), so even a
    // forced ELEVENTY_ENV=production build stays silent. But the guarantee follows the
    // bundle, not the machine: bake one *with* AWS_BRANCH set (re-running the deploy
    // matrix locally) and it reports from wherever it is served — and `yarn dev`
    // serves whatever js:build last emitted, so rebuild before serving.
    enabled: environment !== "development",

    // PII collection is left at its default (off). Cookie consent is not wired up
    // yet — that is #96 — so nothing here should start attaching visitor detail
    // ahead of the CMP that is meant to gate it. Session Replay is omitted for the
    // same reason.
});
